/**
 * Member Payout History Component
 * Member-facing view of their incentive payout history
 * Trust Marketplace - Phase 2: Financial Ledger System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  PayoutStatusIcon,
  countWhere,
  isDeferred,
  isFailed,
  isInFlight,
  isPaid,
  normalizePayoutStatus,
  payoutStatusColor,
  payoutStatusLabel,
  sumWhere,
} from './payoutStatus';
import { PayoutDeferralNotice, type PayoutDeferral } from './PayoutDeferralNotice';
import {
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  AlertCircle,
  RefreshCw,
  Download
} from 'lucide-react';

interface MemberPayoutHistoryProps {
  memberId: number;
}

interface MemberPayout {
  id: number;
  payout_number: string;
  payout_amount: number;
  deferral?: PayoutDeferral | null;
  payout_method: string;
  status: string;
  initiated_date: string;
  scheduled_date?: string;
  processed_date?: string;
  payment_processor_reference?: string;
  approval_notes?: string;
}

const PAYOUT_METHODS: { [key: string]: string } = {
  'CARD': 'Prepaid Card',
  'DIRECT_DEPOSIT': 'Direct Deposit',
  'CHECK': 'Check',
  'CASH': 'Cash'
};

const MemberPayoutHistory: React.FC<MemberPayoutHistoryProps> = ({ memberId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [payouts, setPayouts] = useState<MemberPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPayouts();
  }, [memberId]);

  const fetchPayouts = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getMemberToken();
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }

      // HER OWN PAYOUTS, FROM THE ROUTE THAT CAN ONLY RETURN HER OWN.
      //
      // This used to call the STAFF endpoint with `?member_id=`, which scoped
      // nothing: the member id came from the page, so the response was whatever
      // member was asked for, and that endpoint also returns the member's name
      // beside the amount. /api/member-portal/payouts resolves the member from
      // the token instead, so there is no id to change.
      const url = getWeaveinApiUrl('/api/member-portal/payouts');

      // Member-portal auth: send the MEMBER token + active-org X-CBO-ID.
      // (authFetch reads the staff `auth_token`, which a portal member does not
      // have — that left this tab unauthorized/empty for real members.)
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error(`Failed to fetch payouts: ${response.status}`);
      }

      // The portal route wraps its rows; the old staff route returned a bare
      // array. Accept both so this does not break if either shape appears.
      const data = await response.json();
      setPayouts(Array.isArray(data) ? data : (data?.payouts ?? []));
    } catch (err) {
      console.error('Error fetching payouts:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.dateTime(dateString);
  };

  // Calculate summary metrics
  const totalAmount = payouts.reduce((sum, p) => sum + p.payout_amount, 0);
  // isInFlight covers 'deferred' too: a scheduled payment is money she is
  // still owed, and leaving it out of both totals made it disappear.
  const pendingAmount = sumWhere(payouts, isInFlight);
  const completedAmount = sumWhere(payouts, isPaid);
  const pendingCount = countWhere(payouts, isInFlight);
  const paidCount = countWhere(payouts, isPaid);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6 bg-red-50 border-red-200">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchPayouts}
          className="mt-4"
        >
          {t('financial.try_again', 'Try Again')}
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('financial.payment_history', 'Payment History')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('financial.track_your_incentive_payments_and', 'Track your incentive payments and earnings')}</p>
        </div>
        <Button variant="outline" onClick={fetchPayouts}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('financial.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-xs text-green-700 dark:text-green-300 font-medium">{t('financial.total_received', 'Total Received')}</span>
          </div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">{formatCurrency(completedAmount)}</div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
            {paidCount} payment{paidCount !== 1 ? 's' : ''}
          </div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border-yellow-200 dark:border-yellow-800">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
            <span className="text-xs text-yellow-700 dark:text-yellow-300 font-medium">{t('financial.pending', 'Pending')}</span>
          </div>
          <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">{formatCurrency(pendingAmount)}</div>
          <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">{pendingCount} in progress</div>
        </Card>

        <Card className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs text-blue-700 dark:text-blue-300 font-medium">{t('financial.total_earnings', 'Total Earnings')}</span>
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{formatCurrency(totalAmount)}</div>
          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">{payouts.length} transaction{payouts.length !== 1 ? 's' : ''}</div>
        </Card>
      </div>

      {/* Payouts List */}
      {payouts.length === 0 ? (
        <Card className="p-12 text-center">
          <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('financial.no_payments_yet', 'No payments yet')}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('financial.your_incentive_payments_will_appear', 'Your incentive payments will appear here as you complete program activities')}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {payouts.map(payout => (
            <Card key={payout.id} className="p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <PayoutStatusIcon status={payout.status} />
                </div>

                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {formatCurrency(payout.payout_amount)}
                        </h4>
                        <Badge className={payoutStatusColor(payout.status)}>
                          {payoutStatusLabel(payout.status)}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                        <span>{payout.payout_number}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3" />
                          {PAYOUT_METHODS[payout.payout_method] || payout.payout_method}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>Initiated: {formatDate(payout.initiated_date)}</span>
                    {payout.scheduled_date && (
                      <>
                        <span>→</span>
                        <span>Scheduled: {formatDate(payout.scheduled_date)}</span>
                      </>
                    )}
                    {payout.processed_date && (
                      <>
                        <span>→</span>
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          Processed: {formatDate(payout.processed_date)}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Status Messages */}
                  {payout.deferral && <PayoutDeferralNotice deferral={payout.deferral} tone="member" />}

                  {normalizePayoutStatus(payout.status) === 'pending' && !payout.deferral && (
                    <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                      <span className="text-sm text-yellow-800 dark:text-yellow-200">
                        {t('financial.your_payment_is_being_reviewed', 'Your payment is being reviewed and will be processed soon.')}
                      </span>
                    </div>
                  )}

                  {normalizePayoutStatus(payout.status) === 'processing' && (
                    <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <span className="text-sm text-blue-800 dark:text-blue-200">
                        {t('financial.your_payment_is_being_processed', 'Your payment is being processed and will arrive soon.')}
                      </span>
                    </div>
                  )}

                  {isDeferred(payout.status) && !payout.deferral && (
                    <div className="mt-3 p-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg flex items-center gap-2">
                      <Clock className="w-4 h-4 text-sky-600 dark:text-sky-400 flex-shrink-0" />
                      <span className="text-sm text-sky-800 dark:text-sky-200">
                        {t('financial.payment_is_scheduled', 'This payment is scheduled. Nothing is wrong and you are not losing it.')}
                      </span>
                    </div>
                  )}

                  {isFailed(payout.status) && (
                    <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                      <span className="text-sm text-red-800 dark:text-red-200">
                        {t('financial.payment_failed_please_contact_support', 'Payment failed. Please contact support for assistance.')}
                      </span>
                    </div>
                  )}

                  {isPaid(payout.status) && payout.payment_processor_reference && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Reference: {payout.payment_processor_reference}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Help Text */}
      <Card className="p-5 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('financial.about_your_payments', 'About Your Payments')}</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Payments are typically processed within 3-5 business days after approval. You'll receive a notification when your payment is on the way. If you have questions about a payment, please contact your case manager.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MemberPayoutHistory;
